import { usePublicLanguage } from '../public-site/PublicLanguage';
import React, { Suspense, useEffect, useState } from 'react';
import PublicLayout, { PublicThemeProps } from '../public-site/PublicLayout';
import GamesEntry from '../public-site/GamesEntry';
import { GAME_PAGES, gamePath } from '../public-site/routes';
import { publicRoute } from '../public-site/locales';
import type { GameKind } from '../games/types';
import '../games/games.css';

export const GAMES_TITLE = 'Bible games | Revelationary';
export const GAMES_DESCRIPTION =
  'Play daily Bible Wordle, complete Bible verses and practise scripture with multiple-choice flashcards in your chosen translation.';
export default function GamesPage({
  game,
  leaderboardView = false,
  ...props
}: PublicThemeProps & {
  game?: GameKind;
  leaderboardView?: boolean;
}) {
  const { t, href } = usePublicLanguage();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
    if (game && publicRoute(window.location.pathname).path.replace(/\/$/, '') === '/games') {
      window.history.replaceState(null, '', href(gamePath(game, leaderboardView)));
    }
  }, [game, leaderboardView, href]);
  const page = game ? GAME_PAGES[game] : null;
  const loading = <p role="status">{t('Loading game…')}</p>;
  return (
    <PublicLayout {...props}>
      <section className="games-page home-width">
        <header className="games-heading">
          <h1>{page ? t(page.title) : '/games'}</h1>
          <p>{t(page?.description || 'Read. Remember. Return.')}</p>
        </header>
        {game ? (
          <>
            {!ready && (
              <p>
                <a href={href('/games/')}>{t('← All games')}</a>
              </p>
            )}
            {ready ? (
              <Suspense fallback={loading}>
                <GamesEntry initialGame={game} leaderboardView={leaderboardView} />
              </Suspense>
            ) : (
              loading
            )}
          </>
        ) : (
          <>
            <p className="games-intro">
              {t('A little practice, every day. Explore scripture through three free Bible games.')}
            </p>
            <div className="games-selection">
              <article>
                <span className="game-number">{t('01 / DAILY')}</span>
                <img
                  className="game-cover"
                  src="/games/wordle-illustration.webp"
                  alt={t('Green and brown word-game tiles beside an open Bible.')}
                  width="768"
                  height="512"
                  loading="lazy"
                  decoding="async"
                />
                <h2>{t('Bible Wordle')}</h2>
                <p>
                  {t(
                    'One five-letter Bible word in English. Six guesses. A new shared puzzle every day.',
                  )}
                </p>
                <a className="game-card-link" href={href(gamePath('wordle'))}>
                  {t('Play today →')}
                </a>
              </article>
              <article>
                <span className="game-number">{t('02 / PRACTICE')}</span>
                <img
                  className="game-cover"
                  src="/games/blank-illustration.webp"
                  alt={t('A blue and yellow verse exercise beside an open Bible.')}
                  width="768"
                  height="512"
                  loading="lazy"
                  decoding="async"
                />
                <h2>{t('Fill in the blank')}</h2>
                <p>
                  {t(
                    'Complete a verse from your chosen translation. Pick the missing word from three answers.',
                  )}
                </p>
                <a className="game-card-link" href={href(gamePath('blank'))}>
                  {t('Complete a verse →')}
                </a>
              </article>
              <article>
                <span className="game-number">{t('03 / REMEMBER')}</span>
                <img
                  className="game-cover"
                  src="/games/flashcards-illustration.webp"
                  alt={t('Purple scripture memory cards on a warm brown study desk.')}
                  width="768"
                  height="512"
                  loading="lazy"
                  decoding="async"
                />
                <h2>{t('Verse flashcards')}</h2>
                <p>
                  {t(
                    'Match scripture with its reference in ten-card rounds, with three difficulty levels and a review of missed cards.',
                  )}
                </p>
                <a className="game-card-link" href={href(gamePath('flashcards'))}>
                  {t('Practise verses →')}
                </a>
              </article>
            </div>
            <p className="games-note">
              {t(
                'Play as a guest. Sign in to save completed Wordle scores, earn trophies and join the optional daily leaderboard. Practice games do not change your reading streak.',
              )}
            </p>
          </>
        )}
        <p className="games-study">
          <a href={href('/study/')}>{t('Open the Bible study tool →')}</a>
        </p>
      </section>
    </PublicLayout>
  );
}
